/**
 * Publish Rules ID Management
 * 
 * Ensures every listing rule node (rule + group) has a stable `id` field.
 * Used for patch-based revert operations and future-proofing.
 */

import { v4 as uuidv4 } from 'uuid'
import type { LRRule, LRGroup } from '@/types/appTypes'

/**
 * Ensure every node in a publish rules tree has an `id`.
 * Mutates in place for efficiency, but returns the same ref for chaining.
 * 
 * @param node - Rule or Group node
 * @returns The same node (with ids ensured)
 */
export function ensureListingRuleIds<T extends LRRule | LRGroup>(node: T): T {
  if (!node || typeof node !== 'object') return node
  
  // Ensure current node has id
  if (!node.id || typeof node.id !== 'string' || node.id.trim().length === 0) {
    ;(node as any).id = uuidv4()
  }
  
  // Recursively ensure ids for nested rules (groups)
  if (node.type === 'group' && Array.isArray(node.rules)) {
    for (const child of node.rules) {
      ensureListingRuleIds(child)
    }
  }
  
  return node
}

/**
 * Ensure IDs for an entire publish rules config.
 * Mutates in place.
 */
export function ensureListingRulesConfigIds(config: any): void {
  if (!config || typeof config !== 'object') return
  
  for (const segmentId of Object.keys(config)) {
    const segment = config[segmentId]
    const rules = segment?.inventory_membership_rules?.ruleset?.rules
    if (Array.isArray(rules)) {
      for (const rule of rules) {
        ensureListingRuleIds(rule)
      }
    }
  }
}

/**
 * Strip `id` from listing rule nodes for semantic comparison.
 * Returns a new tree (does not mutate original).
 */
export function stripListingRuleIds<T extends LRRule | LRGroup>(node: T): T {
  if (!node || typeof node !== 'object') return node
  
  const copy = { ...node }
  delete (copy as any).id
  
  if (copy.type === 'group' && Array.isArray(copy.rules)) {
    copy.rules = copy.rules.map(stripListingRuleIds)
  }
  
  return copy
}
